import configLib from '../../../configLib';
import { measurePerf } from '../../debug/performance';
import { parsingResponseFailed } from '../../store/gateway';
import { updateNBBOPricesCache } from '../../store-util/NBBOPriceCache';
import { parseGatewayData } from '../../util/GatewayHelpers';
import { dataArrayToMap } from '../../util/MarketDataHelpers';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { GatewayRequestType, GatewayResponseSubType, GatewayResponseType } from '../web-socket/types';

import { BaseSymbolSubscriptionService } from './BaseSymbolSubscription.service';
import { SymbolSubscriptionRequestType } from './types';

const LOG_PREFIX = '[NBBO-SUBS]';
const logger = LoggerService.getLogger();

/**
 * NBBO Subscription Service
 */
export class NBBOSubscriptionService extends BaseSymbolSubscriptionService<SymbolSubscriptionRequestType> {
  private static instance: NBBOSubscriptionService | undefined;

  // Make the constructor private, to have a single instance
  // eslint-disable-next-line no-useless-constructor
  private constructor() {
    super();
  }

  static getInstance(): NBBOSubscriptionService {
    this.instance ??= new NBBOSubscriptionService();
    return this.instance;
  }

  public handleResponseEvent = (event: WebSocketEventData<any>): void => {
    // Only handle NBBO subscription responses for market data
    if (
      event.data.messageType !== GatewayResponseType.MarketData
      || event.data.messageSubType !== GatewayResponseSubType.Nbbo
    ) {
      return;
    }

    const data = event.data ?? {};
    let parsedData;

    try {
      parsedData = measurePerf('nbbo.parseGatewayData', undefined, () => parseGatewayData(data.arrayData));
    } catch (error) {
      configLib.store.dispatch(parsingResponseFailed(`${error}`));
      return;
    }

    logger.log(`${LOG_PREFIX} NBBOSubscriptionService response! data:`, event);
    const parsedRows = parsedData;
    parsedData = {
      raw: data,
      final: measurePerf(
        'nbbo.dataArrayToMap',
        { rows: parsedRows?.length ?? 0 },
        () => dataArrayToMap(parsedRows),
      ),
    };

    measurePerf('nbbo.updateCache', undefined, () => updateNBBOPricesCache(parsedData.final, 'nbbo'));
  };

  protected shouldHandleResponse(event: WebSocketEventData<any>): boolean {
    return event.data?.messageSubType === GatewayResponseSubType.Nbbo;
  }

  protected getResponseType(): GatewayResponseType {
    return GatewayResponseType.MarketData;
  }

  protected getSubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.MarketDataSubscribeNbbo;
  }

  protected getUnsubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.MarketDataUnsubscribeNbbo;
  }
}
